import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserProfileApi } from '../api/userProfile';
import { getCachedOrFetch, isEntraObjectId, setCachedProfile } from '../utils/userProfileCache.utils';
import type { UpdateUserProfileRequest, UserProfile } from '../validation/userProfileSchemas';

export const useUserProfile = (userId: number | string | null | undefined) => {
  return useQuery<UserProfile | null, Error>({
    queryKey: ['user-profile', userId],
    queryFn: async () => {
      if (typeof userId === 'string' && isEntraObjectId(userId)) {
        return getCachedOrFetch(userId);
      }
      return UserProfileApi.get(userId as number | string);
    },
    enabled: !!userId,
    staleTime: 15 * 60 * 1000, // 15 min — avoid immediate refetch after returning cached secure data
  });
};

export const useUpdateUserProfile = (userId: number | string | null | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<UserProfile | null, Error, UpdateUserProfileRequest>({
    mutationFn: (payload) => UserProfileApi.update(userId as number | string, payload),
    onSuccess: async (data) => {
      queryClient.setQueryData(['user-profile', userId], data);
      const entraId =
        data?.entraObjectId ??
        (typeof userId === 'string' && isEntraObjectId(userId) ? userId : null);
      if (entraId && data) {
        try {
          await setCachedProfile(entraId, data);
        } catch {
          // Non-fatal; React Query cache is already updated
        }
      }
    },
  });
};


